"use client";

import {
  ReactNode,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Facet } from "../types/filters.type";
import { FilterContext } from "./MoreFilters.provider";

export interface QuickFilterProviderHandle {
  getSelections(): string[];
  clear(): void;
}

interface QuickFilterProviderProps {
  facet: Facet;
  initialSelections: string[];
  children: ReactNode;
}

export const QuickFilterProvider = forwardRef<
  QuickFilterProviderHandle,
  QuickFilterProviderProps
>(({ facet, initialSelections, children }, ref) => {
  const [selections, setSelectionsState] =
    useState<string[]>(initialSelections);
  const selectionsRef = useRef(selections);

  const setSelections = useCallback((values: string[]) => {
    selectionsRef.current = values;
    setSelectionsState(values);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      getSelections: () => selectionsRef.current,
      clear: () => setSelections([]),
    }),
    [setSelections],
  );

  const value = useMemo(
    () => ({
      getSelectedValues: (field: string) =>
        field === facet.field ? selections : [],
      onSelectionChange: (_field: string, values: string[]) =>
        setSelections(values),
    }),
    [facet.field, selections, setSelections],
  );

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
});

QuickFilterProvider.displayName = "QuickFilterProvider";
